<template>
  <div class="date-input" :class="side" ref="dateinput">
    <app-input
      :disabled="disabled"
      :label="label"
      :name="valueKey"
      :valueKey="valueKey || name || inputId "
      :onFocus="openDatePicker"
      :onBlur="handleBlur"
      v-model.trim="dateForInput"
      :required="required"
      :invalid="invalid"
      :errorMessage="errorMessage"
    />
    <transition name="slide-fade">
      <v-date-picker v-if="datePickerOpen"
        v-model.trim="dateForPicker"
        no-title
        class="datepicker"
        @hook:updated="datePickerUpdated"
        @hook:mounted="datePickerUpdated"
        ></v-date-picker>
    </transition>
    <button
      v-if="!disabled && !datePickerOpen"
      type="button"
      class="show-calendar-button -strip-style"
      @click="openDatePicker"
      tabindex="-1"
    >
      <font-awesome-icon :icon="['far', 'calendar-alt']" aria-label="Open date picker"></font-awesome-icon>
    </button>
    <button
      v-else-if="!disabled && datePickerOpen"
      type="button"
      class="show-calendar-button -strip-style"
      @click="closeAndEmitValue"
      tabindex="-1"
    >
      <font-awesome-icon :icon="['fas', 'times']" aria-label="Close date picker"></font-awesome-icon>
    </button>
  </div>
</template>
<script>
  import AppInput from '@atoms/InputForDate'
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
  import formattable from '@mixins/formattable'
  import moment from 'moment'

  export default {
    mixins: [formattable],
    components: {
      FontAwesomeIcon,
      AppInput
    },
    props: {
      label: String,
      name: String,
      value: String,
      valueKey: String,
      inputId: String,
      disabled: {
        type: Boolean,
        default: false
      },
      required: {
        type: Boolean,
        default: false
      },
      invalid: {
        type: Boolean,
        default: false
      },
      errorMessage: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        datePickerOpen: false,
        leftSide: false,
        rightSide: false,
        suggestedLocalDateFromInput: ''
      }
    },
    methods: {
      validIso8601DateFormat(date) {
        return date.match(/^\d{4,4}-\d{2,2}-\d{2,2}.*$/g)
      },
      validLocalDateFormat(date) {
        return date.match(/^\d{2,2}\/\d{2,2}\/\d{4,4}$/g)
      },
      handleBlur(e) {
        if (this.suggestedLocalDateFromInput && !this.validLocalDateFormat(this.suggestedLocalDateFromInput)) {
          this.suggestedLocalDateFromInput = ''
          this.internalValue = ''
        }

        if (!this.$refs.dateinput.contains(e.relatedTarget)) {
          this.closeDatePicker()
        }
      },
      closeDatePicker() {
        this.datePickerOpen = false
      },
      openDatePicker() {
        this.datePickerOpen = true
      },
      closeAndEmitValue() {
        const valueToEmit = this.validIso8601DateFormat(this.internalValue) ? this.internalValue : ''

        this.$emit('input', valueToEmit)
        this.closeDatePicker()
      },
      datePickerUpdated() {
        Array.from(this.$el.querySelector('.datepicker').querySelectorAll('*')).forEach(el => {
          el.tabIndex = -1
        })
      }
    },
    computed: {
      dateForInput: {
        get() {
          if (this.suggestedLocalDateFromInput) {
            return this.suggestedLocalDateFromInput
          }

          return this.validIso8601DateFormat(this.internalValue) ? this.toLocalDate(this.internalValue) : ''
        },
        set(val) {
          // the value of whatever we're typing in the input box is set/get in suggestedLocalDateFormInput (in xx/xx/xxxx format)
          // until the date is in the correct format and determined by moment.js to exist (eg: will reject 02/30/2019 and 20/20/2019)
          // Only valid dates in YYYY-MM-DD format or '' are ever stored to internalValue and emitted
          this.suggestedLocalDateFromInput = val

          if (val.trim() === '') {
            this.internalValue = ''
            return
          }

          // make sure the date is in correct local format (MM/DD/YYYY),
          if (!this.validLocalDateFormat(val)) {
            // auto append forward slashes while typing. Eg: 02 becomes 02/ and 02/02 becomes 02/02/
            if (val.match(/^\d{2,2}$/g)) {
              this.suggestedLocalDateFromInput = `${val}/`
            }

            if (val.match(/^\d{2,2}\/\d{2,2}$/g)) {
              this.suggestedLocalDateFromInput = `${val}/`
            }

            return
          }

          // if the date is in the correct local format (MM/DD/YYYY), check to make sure the date exists (eg: not 02/30/2019 and not 20/20/2020)
          // if it doesn't exist clear the input
          if (!moment(val, 'MM/DD/YYYY', true).isValid()) {
            this.internalValue = ''
            this.suggestedLocalDateFromInput = ''
            return
          }

          this.internalValue = this.toIso8601Date(val)
          this.suggestedLocalDateFromInput = ''

          // any time a new value is completed, even if it's from clicking a suggestion, close any open picker
          this.closeDatePicker()
        }
      },
      dateForPicker: {
        get() {
          return this.validIso8601DateFormat(this.internalValue) ? this.internalValue : ''
        },
        set(val) {
          if (!this.validIso8601DateFormat(val)) {
            return
          }

          this.internalValue = val

          this.closeDatePicker()
        }
      },
      side() {
        return {
          'left-side': this.leftSide,
          'right-side': this.rightSide
        }
      },
      internalValue: {
        get () {
          return this.value ? this.value : ''
        },
        set (newVal) {
          this.$emit('input', newVal)
        }
      }
    },
    watch: {
      value(newValue) {
        if (newValue.trim() === '' || this.validIso8601DateFormat(newValue)) {
          this.internalValue = newValue
        } else if (this.validLocalDateFormat(newValue)) {
          this.internalValue = this.toIso8601Date(newValue)
        } else {
          console.warn(`tried to pass invalid date as prop: ${newValue}`)
        }
      },
      suggestedLocalDateFromInput(val) {
        this.$emit('suggestedLocalDateChanged', val)
      }
    },
    mounted() {
      const input = this.$refs.dateinput
      if (!input) {
        return
      }

      const center = input.offsetLeft + input.offsetWidth / 2
      if (center < window.innerWidth / 2) {
        this.leftSide = true
      } else {
        this.rightSide = true
      }
    }
  }
</script>
<style lang="scss">
.datepicker * {
  &:focus {
    outline:none;
  }
}
</style>
